import { useEffect, useState } from "react";
import type { OnyxoLeadDraft } from "./OnyxoBusinessChat";
import type { FormEvent } from "react";

type QuizOption = { value: string; label: string };
type LeadQuizProps = {
  service: string;
  source: string;
  options: QuizOption[];
  initialChoice?: string;
  initialGoal?: string;
  projectPlaceholder?: string;
  heading?: string;
};

export default function LeadQuiz({
  service, source, options, initialChoice, initialGoal = "",
  projectPlaceholder = "např. moje-firma.cz", heading = "Krátký kvíz k vaší poptávce"
}: LeadQuizProps) {
  const [step, setStep] = useState(0);
  const [choice, setChoice] = useState(initialChoice ?? options[0]?.value ?? "");
  const [project, setProject] = useState("");
  const [goal, setGoal] = useState(initialGoal);
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState<"idle" | "sending" | "sent" | "error">("idle");
  const activeLabel = options.find((option) => option.value === choice)?.label ?? choice;

  useEffect(() => {
    const applyDraft = (event: Event) => {
      const value = (event as CustomEvent<OnyxoLeadDraft>).detail;
      if (!value || typeof value.brief !== "string" || typeof value.service !== "string") return;
      const brief = value.brief.trim().slice(0, 500);
      if (!brief) return;
      // Pre-fill only. A visitor must review the complete form and explicitly submit it.
      setGoal(current => current.trim() ? current : "Dotaz ONYXO: " + brief);
      if (value.service === "automation" && options.some(o => o.value === "automation")) setChoice("automation");
      if (value.service === "web-design" && options.some(o => o.value === "redesign") && value.topic === "redesign") setChoice("redesign");
      setStep(1);
      setStatus("idle");
    };
    window.addEventListener("onyxo:lead-draft", applyDraft);
    return () => window.removeEventListener("onyxo:lead-draft", applyDraft);
  }, [options]);

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (step < 2) {
      if (step === 0 && !choice) return;
      if (step === 1 && (!project.trim() || !goal.trim())) return;
      setStep(step + 1);
      setStatus("idle");
      return;
    }
    if (!project.trim() || !goal.trim() || !email.trim()) return;
    setStatus("sending");
    try {
      const response = await fetch("/api/audit", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({
          service,
          source,
          project: project.trim(),
          email: email.trim(),
          goal: `Vybraná možnost: ${activeLabel}. ${goal.trim()}`
        })
      });
      if (response.status !== 201) throw new Error("lead_not_created");
      const result: unknown = await response.json();
      if (typeof result !== "object" || result === null ||
          !("ok" in result) || result.ok !== true ||
          !("id" in result) || typeof result.id !== "string") {
        throw new Error("lead_not_persisted");
      }
      setStatus("sent");
    } catch {
      setStatus("error");
    }
  }

  return (
    <form className="onyx-lead-quiz" onSubmit={submit} aria-label={heading}>
      <div className="quiz-heading">
        <span>NEZÁVAZNÁ POPTÁVKA · {step + 1}/3</span>
        <strong>{heading}</strong>
      </div>
      <div className="quiz-progress" aria-label={`Krok ${step + 1} ze 3`}>
        {[0, 1, 2].map((number) => <span key={number} className={number <= step ? "active" : ""} />)}
      </div>
      {status === "sent" ? (
        <div className="quiz-success" role="status">
          <strong>✓ Poptávka byla přijata.</strong>
          <p>Děkujeme. Vaše zadání bylo uloženo a můžeme se vám ozvat s dalším postupem.</p>
        </div>
      ) : (
        <>
          {step === 0 && (
            <fieldset className="quiz-fieldset">
              <legend>Co chcete nejdříve vyřešit?</legend>
              <div className="quiz-options">
                {options.map((option) => (
                  <button className={"quiz-option" + (choice === option.value ? " active" : "")}
                    key={option.value} type="button" aria-pressed={choice === option.value}
                    onClick={() => setChoice(option.value)}>
                    <span>{option.label}</span><b aria-hidden="true">{choice === option.value ? "✓" : "→"}</b>
                  </button>
                ))}
              </div>
            </fieldset>
          )}
          {step === 1 && (
            <div className="quiz-fields">
              <label>Váš podnik, projekt nebo URL
                <input name="project" autoComplete="organization" value={project} onChange={(e) => setProject(e.target.value)}
                  placeholder={projectPlaceholder} maxLength={300} required />
              </label>
              <label>Co má řešení zlepšit?
                <textarea name="goal" value={goal} onChange={(e) => setGoal(e.target.value)}
                  placeholder="Stačí několik vět. Co vás nyní nejvíce brzdí?" maxLength={3800} required />
              </label>
            </div>
          )}
          {step === 2 && (
            <div className="quiz-fields">
              <p className="quiz-summary"><b>Vaše zadání:</b> {activeLabel} · {project}</p>
              <label>Kam vám můžeme napsat?
                <input name="email" type="email" autoComplete="email" value={email} onChange={(e) => setEmail(e.target.value)}
                  placeholder="vas@email.cz" maxLength={254} required />
              </label>
              <small>Kontaktní údaje použijeme k vyřízení vaší poptávky.</small>
            </div>
          )}
          <div className="quiz-actions">
            {step > 0 && <button type="button" className="quiz-back" onClick={() => { setStep(step - 1); setStatus("idle"); }}>← Zpět</button>}
            <button className="quiz-next" type="submit" disabled={status === "sending" || (step === 0 && !choice)}>
              {status === "sending" ? "Odesílám…" : step === 2 ? "Odeslat nezávaznou poptávku →" : "Pokračovat →"}
            </button>
          </div>
          {status === "error" && <p className="quiz-error" role="alert">Odeslání se nepodařilo. Zkuste to prosím znovu.</p>}
        </>
      )}
    </form>
  );
}
